import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CompanyType } from '@/types/company';
import type { IndustryContract, CreateContractInput, UpdateContractInput } from '@/types/industry';
import { useToast } from '@/components/ui/use-toast';

export function useIndustryContracts(domain: CompanyType) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: company } = useQuery({
    queryKey: ['current_company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data } = await supabase.from('companies').select('*').eq('user_id', user!.id).single();
      return data;
    },
  });

  const { data: contracts, isLoading } = useQuery({
    queryKey: ['industry_contracts', company?.id, domain],
    queryFn: async () => {
      if (!company) return [];
      const { data } = await supabase
        .from('industry_contracts')
        .select('*')
        .eq('company_id', company.id)
        .eq('domain', domain);
      return (data || []) as IndustryContract[];
    },
    enabled: !!company,
  });

  const createContract = useMutation({
    mutationFn: async (input: CreateContractInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data } = await supabase
        .from('industry_contracts')
        .insert({ ...input, domain, company_id: company!.id, user_id: user!.id })
        .select()
        .single();
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['industry_contracts'] });
      toast({ title: 'Contrato criado' });
    },
  });

  return { contracts: contracts || [], isLoading, createContract: createContract.mutate };
}
